import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Brain, Zap, Waves, Droplets, Pill, Activity, BadgeCheck,
  ShieldCheck, Languages, Syringe,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";
import useSEO from "@/hooks/useSEO";

const HERO = "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/5e82a9f722c7e3ced8097f75e2d29d76a5215f8b737bbc90c1e97f4013c9f8e8.jpeg";
const SITE = "https://manasneuropsychiatryhospital.in";

const therapies = [
  {
    icon: Brain,
    tag: "સૌથી વધુ પુરાવા · FDA માન્ય",
    title: "rTMS — ચુંબકીય તરંગ સારવાર",
    to: "/therapies/rtms",
    desc: "માથા પર મૂકેલી કોઈલ દ્વારા ચુંબકીય તરંગો મગજના ચોક્કસ ભાગને ફરી સક્રિય કરે છે. બેહોશી નહીં, ઓપરેશન નહીં, દાખલ થવાની જરૂર નહીં. દરરોજ ૩૦ મિનિટની બેઠક — પછી તમે સીધા કામ પર જઈ શકો. દવાથી ન મટતી હતાશા, OCD, ચિંતા અને તમાકુ છોડાવવા માટે માન્ય.",
  },
  {
    icon: Zap,
    tag: "બેહોશીમાં · સંપૂર્ણ દેખરેખ હેઠળ",
    title: "mBPT — આધુનિક ‘શોક’ સારવાર",
    to: "/therapies/mbpt",
    desc: "ફિલ્મોમાં દેખાતી જૂની સારવાર નહીં. આજની mBPT સંપૂર્ણ બેહોશી અને સ્નાયુ શિથિલ કરતી દવા સાથે અપાય છે — શરીરમાં ઝટકા દેખાતા નથી, દર્દીને કંઈ યાદ રહેતું નથી અને દુખાવો થતો નથી. જીવ જોખમમાં હોય તેવી તીવ્ર હતાશા, ખાવાનું બંધ કરી દેવું, કેટાટોનિયા અને આત્મહત્યાના તીવ્ર વિચારોમાં સૌથી ઝડપી રાહત.",
  },
  {
    icon: Waves,
    tag: "સૌથી હળવી · સસ્તી",
    title: "tDCS — હળવો વિદ્યુત પ્રવાહ",
    to: "/therapies/tdcs",
    desc: "માથા પર બે નરમ સ્પોન્જ પટ્ટી મૂકીને ખૂબ જ હળવો પ્રવાહ અપાય છે — દુખાવો નહીં, ફક્ત થોડી ઝણઝણાટી. ૨૦–૩૦ મિનિટની બેઠક દરમિયાન તમે વાંચી પણ શકો. હળવી હતાશા, દારૂ–તમાકુની તલપ, લકવા કે માથાની ઈજા પછીની યાદશક્તિ સુધારવા અને લાંબા સમયના દુખાવામાં ઉપયોગી.",
  },
  {
    icon: Droplets,
    tag: "કલાકોમાં અસર · મોનિટર સાથે",
    title: "કેટામીન IV ઇન્ફ્યુઝન",
    to: "/therapies/ketamine-infusion",
    desc: "શાંત ઓરડામાં, ડૉક્ટરની હાજરીમાં, બ્લડપ્રેશર–પલ્સ–ઓક્સિજન પર સતત નજર રાખીને આશરે ૪૦ મિનિટનું ધીમું ઇન્ફ્યુઝન. દવાથી ન મટતી તીવ્ર હતાશા અને આત્મહત્યાના વિચારોમાં ઘણીવાર ૨૪ કલાકમાં જ રાહત મળે છે — જ્યાં ગોળીની અસર માટે ચાર અઠવાડિયા વાટ જોવી જોખમી હોય.",
  },
  {
    icon: Pill,
    tag: "વ્યસનમુક્તિ · મહિનાઓ સુધી અસર",
    title: "નાલ્ટ્રેક્સોન ઇમ્પ્લાન્ટ",
    to: "/therapies/naltrexone-implant",
    desc: "ચામડી નીચે મૂકાતી નાની ગોળી (ઇમ્પ્લાન્ટ) મહિનાઓ સુધી ધીમે ધીમે દવા છોડે છે — દારૂ કે અફીણ–બ્રાઉન શુગરની તલપ પર સતત રોક લાગે છે. રોજની ગોળી યાદ રાખવાની ઝંઝટ નથી, અને દારૂ છોડ્યા પછીના સૌથી જોખમી પહેલા છ મહિનામાં ફરી લપસી જવાનું જોખમ ખૂબ ઘટે છે.",
  },
];

const why = [
  { icon: Zap, label: "ઓપરેશન વગર", desc: "rTMS અને tDCS માં બેહોશી કે ચીરો નથી. તમે ચાલીને આવો, બેઠક પૂરી કરીને ચાલીને જાઓ." },
  { icon: Activity, label: "દવા વગર કે દવા સાથે", desc: "દવાની આડઅસર સહન ન થાય તેવા દર્દીઓ માટે વિકલ્પ, અને બીજા માટે દવા સાથેનો સહારો." },
  { icon: BadgeCheck, label: "FDA માન્ય પ્રોટોકોલ", desc: "વિશ્વભરના સંશોધન આધારિત નિયત પ્રોટોકોલ — અંદાજ કે અખતરા નહીં." },
  { icon: ShieldCheck, label: "ભાવનગરમાં જ", desc: "કાળાનાળા, ભાવનગરમાં અમારી પોતાની ન્યુરોમોડ્યુલેશન સુવિધા — અમદાવાદ કે મુંબઈ જવાની જરૂર નહીં." },
];

const indications = [
  { title: "દવાથી ન મટતી હતાશા", desc: "બે કે વધુ દવા આપ્યા પછી પણ સુધારો ન થયો હોય તેવા દર્દીઓ માટે." },
  { title: "OCD (વારંવાર થતા વિચાર–ક્રિયા)", desc: "વારંવાર હાથ ધોવા, ચેક કરવા, ગણવા — તીવ્ર કિસ્સાઓ માટે માન્ય rTMS પ્રોટોકોલ." },
  { title: "ચિંતા અને પેનિક એટેક", desc: "સતત ગભરામણ, છાતીમાં ધડકન, મરી જવાનો ડર — લક્ષિત સારવાર." },
  { title: "આત્મહત્યાના તીવ્ર વિચાર", desc: "કેટામીન ઇન્ફ્યુઝન અને mBPT — જ્યાં ઝડપ જ સૌથી મોટી જરૂરિયાત છે." },
  { title: "સ્કિઝોફ્રેનિયામાં અવાજ સંભળાવા", desc: "દવા સાથે ન મટતાં ભ્રમ–અવાજ માટે rTMS/tDCS સહાયક સારવાર." },
  { title: "વ્યસનની તલપ", desc: "દારૂ, તમાકુ અને અફીણની તલપ ઘટાડવા — સાથે નાલ્ટ્રેક્સોન ઇમ્પ્લાન્ટનો વિકલ્પ." },
];

const faqs = [
  { q: "rTMS શું ‘શોક ટ્રીટમેન્ટ’ છે?", a: "નહીં. rTMS માં બેહોશી અપાતી નથી, દર્દી પૂરો જાગતો હોય છે, ઝટકા આવતા નથી અને યાદશક્તિ પર કોઈ અસર થતી નથી. ફક્ત માથા પર ટક-ટક જેવી લાગણી થાય છે. mBPT (આધુનિક શોક સારવાર) સંપૂર્ણપણે અલગ સારવાર છે અને ફક્ત ખૂબ ગંભીર કિસ્સાઓમાં, બેહોશીમાં, અપાય છે." },
  { q: "દુખાવો થાય છે?", a: "rTMS અને tDCS માં દુખાવો થતો નથી — પહેલી બે-ત્રણ બેઠકમાં માથા પર ટક-ટક કે ઝણઝણાટી જેવું અજુગતું લાગે, પછી ટેવાઈ જવાય છે. mBPT બેહોશીમાં અપાય છે, તેથી કંઈ અનુભવાતું નથી." },
  { q: "કેટલી બેઠકો જોઈએ?", a: "હતાશા માટે rTMS ની સામાન્ય સારવાર ૪–૬ અઠવાડિયામાં ૨૦–૩૦ બેઠકો; tDCS માં ૧૦–૨૦ બેઠકો; કેટામીન માટે ૨–૩ અઠવાડિયામાં આશરે ૬ ઇન્ફ્યુઝન; mBPT માં સામાન્યપણે ૬–૧૦ સેશન. શરૂ કરતાં પહેલાં તમને બેઠકોની સંખ્યા અને ખર્ચ લખીને જણાવવામાં આવે છે." },
  { q: "દાખલ થવું પડશે?", a: "rTMS, tDCS અને કેટામીન ઇન્ફ્યુઝન સામાન્યપણે OPD/ડે-કેરમાં થાય છે — તે જ દિવસે ઘરે. mBPT માટે શરૂઆતના સેશન દરમિયાન દાખલ થવું જરૂરી હોય છે." },
  { q: "દવા બંધ કરવી પડશે?", a: "નહીં. ન્યુરોમોડ્યુલેશન સામાન્યપણે ચાલુ દવા સાથે જ અપાય છે, અને ઘણીવાર તેના કારણે દવાનો ડોઝ ઓછો રાખી શકાય છે. દવા બદલવાનો નિર્ણય અલગથી, વિચારીને લેવાય છે." },
  { q: "કોને આ સારવાર ન અપાય?", a: "માથામાં ધાતુનો ટુકડો કે ક્લિપ, કોકલિયર ઇમ્પ્લાન્ટ, હૃદયનું પેસમેકર, અનિયંત્રિત વાઈ કે તાજેતરની માથાની ગંભીર ઈજા હોય તો rTMS/tDCS અપાતું નથી. દરેક દર્દીની પહેલી મુલાકાતમાં આ તપાસ થાય છે." },
  { q: "અસર કાયમી રહે છે?", a: "જે દર્દીઓને ફાયદો થાય તેમાં અસર લાંબી ટકે છે, પણ દવા, થેરાપી અને નિયમિત ફોલો-અપ ચાલુ રાખવા જરૂરી છે. ફરી લક્ષણો દેખાય તો બૂસ્ટર બેઠકો કે ફરી કોર્સ સામાન્યપણે કામ કરે છે." },
  { q: "ખર્ચ કેટલો થાય?", a: "દરેક સારવારનો દર અલગ છે — tDCS સૌથી સસ્તી, rTMS મધ્યમ, કેટામીન અને mBPT માં મોનિટરિંગનો ખર્ચ ઉમેરાય. પહેલી મુલાકાતમાં કુલ કોર્સનો અંદાજ લખીને આપવામાં આવે છે; કોઈ છૂપો ખર્ચ નથી." },
];

const JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalClinic",
      "@id": `${SITE}/#neuromodulation-gu`,
      name: "માનસ ન્યુરોમોડ્યુલેશન સેન્ટર, ભાવનગર",
      alternateName: "Manas Neuromodulation Suite, Bhavnagar",
      medicalSpecialty: "Psychiatry",
      inLanguage: "gu-IN",
      url: `${SITE}/gu/neuromodulation`,
      telephone: "+91-94275-60101",
      foundingDate: "1989",
      availableService: [
        { "@type": "MedicalProcedure", name: "rTMS — Repetitive Transcranial Magnetic Stimulation" },
        { "@type": "MedicalProcedure", name: "Modified Brief Pulse Therapy (mBPT)" },
        { "@type": "MedicalProcedure", name: "tDCS — Transcranial Direct Current Stimulation" },
        { "@type": "MedicalProcedure", name: "Ketamine IV Infusion Therapy" },
        { "@type": "MedicalProcedure", name: "Naltrexone Drug Implant" },
      ],
      address: {
        "@type": "PostalAddress",
        streetAddress: "2nd floor, Carlton Square, Near Madhavdeep Complex, Kalanala",
        addressLocality: "Bhavnagar",
        postalCode: "364001",
        addressRegion: "Gujarat",
        addressCountry: "IN",
      },
      areaServed: ["Bhavnagar", "Sihor", "Palitana", "Talaja", "Mahuva", "Botad", "Amreli", "Saurashtra", "Gujarat"],
      openingHoursSpecification: [{
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "09:00",
        closes: "20:00",
      }],
    },
    {
      "@type": "FAQPage",
      inLanguage: "gu-IN",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export default function NeuromodulationGu() {
  useSEO({
    title: "ભાવનગરમાં rTMS · ન્યુરોમોડ્યુલેશન સારવાર (ગુજરાતી)",
    description:
      "ભાવનગરની માનસ હોસ્પિટલમાં rTMS (ચુંબકીય તરંગ સારવાર), mBPT, tDCS, કેટામીન IV ઇન્ફ્યુઝન અને નાલ્ટ્રેક્સોન ઇમ્પ્લાન્ટ — દવાથી ન મટતી હતાશા, OCD, ચિંતા અને વ્યસન માટે આધુનિક સારવાર. AIIMS દિલ્હીમાં તાલીમ પામેલા TMS ફિઝિશિયન. ફોન +૯૧ ૯૪૨૭૫ ૬૦૧૦૧.",
    image: HERO,
    jsonLd: JSONLD,
    lang: "gu",
    alternates: [
      { hreflang: "en-IN", href: `${SITE}/neuromodulation` },
      { hreflang: "gu-IN", href: `${SITE}/gu/neuromodulation` },
      { hreflang: "x-default", href: `${SITE}/neuromodulation` },
    ],
  });
  const [open, setOpen] = useState(false);

  return (
    <div data-testid="neuromodulation-gu-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <p className="eyebrow text-[#A39171]">ન્યુરોમોડ્યુલેશન · આધુનિક મગજ સારવાર</p>
          <Link
            to="/neuromodulation"
            data-testid="nm-gu-switch-english"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#A39171]/40 text-[#5C524E] hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors"
          >
            <Languages className="w-3.5 h-3.5" /> Read in English
          </Link>
        </div>
        <h1 className="font-display text-4xl md:text-6xl text-[#2D2623] leading-[1.1] tracking-tight max-w-4xl">
          દવા કામ ન કરે ત્યારે <br />
          <span className="italic text-[#E07A5F]">રસ્તો પૂરો થતો નથી</span>.
        </h1>
        <p className="mt-8 text-lg md:text-xl text-[#5C524E] max-w-3xl leading-relaxed">
          ભાવનગરમાં માનસ હોસ્પિટલની અલાયદી ન્યુરોમોડ્યુલેશન સુવિધા — rTMS, mBPT, tDCS, કેટામીન IV
          ઇન્ફ્યુઝન અને નાલ્ટ્રેક્સોન ઇમ્પ્લાન્ટ. જે દર્દીઓને ફક્ત દવાથી પૂરતો ફાયદો ન થયો હોય તેમના
          માટે પુરાવા આધારિત, આધુનિક સારવાર.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Button data-testid="nm-gu-hero-book" onClick={() => setOpen(true)} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-14 font-medium">
            યોગ્યતા જાણવા મુલાકાત લો <ArrowUpRight className="ml-1 w-4 h-4" />
          </Button>
          <a href="tel:+919427560101" data-testid="nm-gu-hero-call" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] text-[#2D2623] px-8 h-14 inline-flex items-center justify-center font-medium transition-colors">
            ફોન +૯૧ ૯૪૨૭૫ ૬૦૧૦૧
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-24">
        <div className="rounded-3xl overflow-hidden aspect-[16/9] md:aspect-[21/9] relative">
          <img src={HERO} alt="માનસ હોસ્પિટલ, ભાવનગરમાં rTMS સારવાર લેતા દર્દી" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/90 via-[#1A1513]/45 to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 md:left-12 md:right-auto md:max-w-xl">
            <p className="eyebrow text-[#F0CF5B] mb-3 drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]">૧૯૮૯થી · કાળાનાળા, ભાવનગર</p>
            <p className="font-display text-2xl md:text-3xl leading-snug text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]">
              ૩૦ મિનિટની બેઠક. બેહોશી નહીં. તે જ દિવસે કામ પર પરત.
            </p>
          </div>
        </div>
      </section>

      {/* THERAPIES */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-14">
            <p className="eyebrow text-[#A39171] mb-4">અમારી પાંચ સારવાર</p>
            <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight leading-snug">
              એક જ છત નીચે <span className="italic text-[#E07A5F]">પાંચ આધુનિક વિકલ્પ</span>.
            </h2>
            <p className="mt-6 text-lg text-[#5C524E] leading-relaxed">
              દરેક સારવાર વિશે વિગતવાર માહિતી — કેવી રીતે કામ કરે છે, કોને અપાય, આડઅસર શું અને કોને ન
              અપાય — નીચે આપેલી લિંક પર (અંગ્રેજીમાં) વાંચી શકાય છે.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {therapies.map((th, i) => (
              <div key={th.title} data-testid={`nm-gu-therapy-${i}`}
                className={`rounded-3xl p-8 md:p-10 border transition-shadow hover:shadow-[0_20px_50px_rgba(163,145,113,0.15)] ${i === 4 ? "md:col-span-2 bg-[#2D2623] text-[#FDFBF7] border-[#FDFBF7]/10" : "bg-[#F4EFE6] border-[#A39171]/15"}`}>
                <span className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-5 ${i % 2 === 0 ? "bg-[#E07A5F] text-white" : i === 4 ? "bg-[#E07A5F] text-white" : "bg-[#2D2623] text-[#FDFBF7]"}`}>
                  <th.icon className="w-5 h-5" />
                </span>
                <p className={`eyebrow mb-2 ${i === 4 ? "text-[#E2C044]" : "text-[#A39171]"}`}>{th.tag}</p>
                <h3 className={`font-display text-2xl md:text-3xl tracking-tight leading-snug ${i === 4 ? "" : "text-[#2D2623]"}`}>{th.title}</h3>
                <p className={`mt-4 leading-relaxed ${i === 4 ? "text-[#FDFBF7]/80" : "text-[#5C524E]"}`}>{th.desc}</p>
                <Link to={th.to} data-testid={`nm-gu-therapy-link-${i}`}
                  className={`mt-6 inline-flex items-center gap-2 font-medium text-sm link-underline ${i === 4 ? "text-[#E2C044] hover:text-[#FDFBF7]" : "text-[#2D2623] hover:text-[#E07A5F]"}`}>
                  વિગતવાર માહિતી વાંચો <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">કેમ ન્યુરોમોડ્યુલેશન</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl leading-snug">
            બીજા દરવાજા બંધ થયા હોય, <span className="italic text-[#E07A5F]">ત્યારે આ દરવાજો ખૂલે છે</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {why.map((w, i) => (
              <div key={w.label} data-testid={`nm-gu-why-${i}`}
                className="bg-[#FDFBF7] rounded-3xl p-7 border border-[#A39171]/15 hover:border-[#E07A5F]/40 transition-all">
                <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] text-white flex items-center justify-center mb-5">
                  <w.icon className="w-5 h-5" />
                </span>
                <h3 className="font-display text-xl text-[#2D2623] leading-snug">{w.label}</h3>
                <p className="text-[#5C524E] mt-2 text-sm leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDICATIONS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">કઈ તકલીફોમાં</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl leading-snug">
            કોને આ સારવારથી <span className="italic text-[#E07A5F]">ફાયદો થાય છે</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {indications.map((ind, i) => (
              <div key={ind.title} data-testid={`nm-gu-ind-${i}`}
                className="bg-[#F4EFE6] rounded-3xl p-8 border border-[#A39171]/15 group hover:shadow-[0_20px_50px_rgba(163,145,113,0.12)] transition-all">
                <Brain className="w-7 h-7 text-[#E07A5F] mb-5 group-hover:scale-110 transition-transform" />
                <h3 className="font-display text-xl md:text-2xl text-[#2D2623] tracking-tight leading-snug">{ind.title}</h3>
                <p className="text-[#5C524E] mt-2 text-sm leading-relaxed">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">rTMS કોર્સ કેવો હોય</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight mb-14 leading-snug">
            પહેલા દિવસથી અંત સુધી, <span className="italic text-[#E07A5F]">પગલું પગલું</span>.
          </h2>
          <ol className="space-y-10">
            {[
              { t: "દિવસ ૦ — તપાસ અને યોગ્યતા", d: "સંપૂર્ણ માનસિક તપાસ, અગાઉ લીધેલી દવાઓની સમીક્ષા, અને માથામાં ધાતુ, પેસમેકર કે વાઈનો ઇતિહાસ છે કે નહીં તેની સુરક્ષા તપાસ. યોગ્ય ન હોય તો અમે સ્પષ્ટ ના પણ કહીએ છીએ." },
              { t: "દિવસ ૧ — મોટર થ્રેશોલ્ડ માપન", d: "તમારા મગજને કઈ તીવ્રતા જોઈએ તે અંગૂઠાની હળવી હલનચલન જોઈને નક્કી થાય છે — ડોઝ તમારા માટે વ્યક્તિગત, સૌને એકસરખો નહીં." },
              { t: "અઠવાડિયું ૧–૪ — મુખ્ય કોર્સ", d: "અઠવાડિયામાં પાંચ દિવસ, રોજ આશરે ૩૦ મિનિટ. તમે જાગતા બેસો છો, વાત કરી શકો છો, અને પછી સીધા કામ કે કૉલેજ જઈ શકો છો." },
              { t: "અઠવાડિયું ૫–૬ — ઘટાડો અને સમીક્ષા", d: "બેઠકો ધીમે ધીમે ઘટાડાય છે અને શરૂઆતના જ સ્કેલ પર સુધારો માપવામાં આવે છે. પછી જાળવણીની યોજના — સામાન્યપણે થોડી બૂસ્ટર બેઠકો." },
            ].map((s, i) => (
              <li key={s.t} data-testid={`nm-gu-step-${i}`} className="grid grid-cols-12 gap-6 border-b border-[#A39171]/15 pb-10 last:border-none">
                <div className="col-span-12 md:col-span-3"><span className="font-display text-4xl text-[#E07A5F]">0{i + 1}</span></div>
                <div className="col-span-12 md:col-span-9">
                  <h3 className="font-display text-xl md:text-3xl text-[#2D2623] tracking-tight leading-snug">{s.t}</h3>
                  <p className="text-[#5C524E] mt-3 text-base md:text-lg leading-relaxed">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* SAFETY NOTE */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div data-testid="nm-gu-safety" className="bg-[#2D2623] text-[#FDFBF7] rounded-3xl p-8 md:p-12 flex flex-col md:flex-row gap-6 items-start relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
            <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] flex items-center justify-center shrink-0 relative">
              <Syringe className="w-5 h-5" />
            </span>
            <div className="relative">
              <p className="eyebrow text-[#E2C044] mb-3">ખાસ વાંચો</p>
              <p className="text-base md:text-lg leading-relaxed text-[#FDFBF7]/90">
                નાલ્ટ્રેક્સોન ઇમ્પ્લાન્ટ મૂક્યા પછી અકસ્માત કે ઓપરેશન વખતે અફીણ-આધારિત દુખાવાની દવા
                અસર કરતી નથી — તેથી હંમેશા મેડિકલ એલર્ટ કાર્ડ સાથે રાખો અને સારવાર કરનાર ડૉક્ટરને
                જણાવો. તેમજ ઇમ્પ્લાન્ટ મૂકતાં પહેલાં દર્દી સંપૂર્ણપણે ડિટોક્સ થયેલો હોવો જરૂરી છે, અને
                તે દર્દીની પોતાની સંમતિથી જ મૂકાય છે — પરિવારના કહેવાથી છૂપી રીતે નહીં.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">વારંવાર પૂછાતા પ્રશ્નો</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight mb-14 leading-snug">
            પરિવારજનો હંમેશા <span className="italic text-[#E07A5F]">આ પ્રશ્ન પૂછે છે</span>.
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`nmgu-${i}`} data-testid={`nm-gu-faq-${i}`}
                className="bg-[#FDFBF7] rounded-2xl border border-[#A39171]/15 px-6 data-[state=open]:border-[#E07A5F]/35 transition-colors">
                <AccordionTrigger className="text-left font-display text-lg md:text-2xl text-[#2D2623] tracking-tight hover:no-underline py-5 leading-snug">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#5C524E] text-base md:text-lg leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#2D2623] text-[#FDFBF7] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center relative">
          <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-snug">
            તમે આ સારવાર માટે યોગ્ય છો કે નહીં, <span className="italic text-[#E07A5F]">એક મુલાકાતમાં ખબર પડશે</span>.
          </h2>
          <p className="text-[#FDFBF7]/70 mt-5 max-w-xl mx-auto">
            ૩૦ મિનિટની યોગ્યતા તપાસ. પ્રામાણિક જવાબ, લખેલો ખર્ચ, કોઈ દબાણ નહીં.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="nm-gu-cta-book" onClick={() => setOpen(true)} className="rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] text-white px-8 h-12 font-medium">સમય માંગો</Button>
            <a href="tel:+919427560101" className="rounded-full border border-[#FDFBF7]/30 hover:bg-[#FDFBF7] hover:text-[#2D2623] px-8 h-12 inline-flex items-center justify-center font-medium transition-colors">ફોન +૯૧ ૯૪૨૭૫ ૬૦૧૦૧</a>
          </div>
        </div>
      </section>

      <AppointmentDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
